import React, { memo, useState } from 'react';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { transactionsSelectors } from 'app-components/flux/transactions';
import { isOtherSideOfSplitTransferTxn } from 'app-components/flux/transactions/splitsHelpers';
import { transactionsUtils } from 'app-components/flux/transactions';

import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import BarChartIcon from '@mui/icons-material/BarChart';
import EditIcon from '@mui/icons-material/Edit';
import LinkOff from '@mui/icons-material/LinkOff';
import MenuButton from '@mui/icons-material/MoreVert';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import RepeatIcon from '@mui/icons-material/RepeatRounded';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import type { ScheduledTransaction } from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import type { CashFlowTransaction } from 'app-components/flux/transactions/transactionsTypes';
import QButton from 'components/QButton';
import QTip from 'components/QuickenControls/QTip';
import { useAppSelector } from 'hooks/redux';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  link: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.primary.main,
    maxWidth: 223,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  seriesLine: {
    display: 'flex',
    paddingRight: 2,
    color: theme.palette.text.secondary,
  },
  spacedIcon: {
    marginRight: 8,
  },
  markAsRecurring: {
    height: 58,
    borderRadius: 12,
    border: `1px solid ${theme.palette.border.light}`,
    boxShadow: '0px -1px 2px 0px rgba(0, 0, 0, 0.05), 0px 2px 4px 0px rgba(0, 0, 0, 0.16)',
  },
  // block (drawer) variant: bordered button matching the split-transactions button, black label text.
  markAsRecurringBlock: {
    width: '100%',
    maxWidth: 'none',
    minHeight: 42,
    padding: '8px 16px',
    borderRadius: 12,
    border: `1px solid ${theme.palette.border.main}`,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.primary.main,
    },
  },
  recurringBlockIcon: {
    height: 22,
    width: 22,
    color: theme.palette.icon.main,
  },
  recurringLabel: {
    display: 'flex',
    maxWidth: 348,
    height: 58,
    borderRadius: 12,
    padding: 4,
    border: `1px solid ${theme.palette.border.light}`,
    boxShadow: '0px -1px 2px 0px rgba(0, 0, 0, 0.05), 0px 2px 4px 0px rgba(0, 0, 0, 0.16)',
    width: '100%',
  },
  imgClass: {
    height: 18,
    width: 18,
    marginRight: 8,
  },
  recurringImgClass: {
    height: 18,
    width: 18,
    marginRight: 4,
    color: theme.palette.icon.light,
  },
  rightArrowClass: {
    height: 12,
    width: 12,
    marginLeft: 2,
  },
  menu: {
    marginLeft: 8,
    borderRadius: '50%',
  },
  light: {
    height: 32,
    width: 32,
    padding: 4,
    color: theme.palette.icon.main,
  },
  recurringText: {
    width: '100%',
    paddingLeft: 10,
    textAlign: 'left',
  },
  boldText: {
    fontWeight: 500,
  },
  displayFlex: {
    display: 'flex',
  },
  // block variant: fill the drawer's single column instead of the header's constrained width.
  fullWidth: {
    width: '100%',
    maxWidth: 'none',
  },
}));

type Props = {
  txn: CashFlowTransaction;
  model: ScheduledTransaction | undefined;
  isBill: boolean | null | undefined;
  unMarkAsBill: () => void;
  markRecurring: () => void;
  viewRecurring: () => void;
  disabledRecurring: boolean;
  setEditSeries: () => void;
  unlinkRecurring: () => void;
  /** 'inline' keeps the header's constrained width; 'block' fills the drawer's single column. */
  variant?: 'inline' | 'block';
};

/**
 * The transaction's recurring/series affordance: "Mark As Recurring" button, the linked-series
 * label with its overflow menu (Unlink / Edit Series / View Series History), or the
 * "Unmark as One-time bill" button. Extracted from TxnHeaderSection so the redesigned drawer body
 * can reuse the exact same behavior with full-width styling.
 */
function RecurringLinkRow({
  txn,
  model,
  isBill,
  unMarkAsBill,
  markRecurring,
  viewRecurring,
  disabledRecurring,
  setEditSeries,
  unlinkRecurring,
  variant = 'inline',
}: Props): React.JSX.Element | null {
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null);
  const { classes, cx } = useStyles();

  const splitSeriesEnabled = useAppSelector(
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'splitSeries') ?? false,
  );
  const hideUnlinkSeriesOption = useAppSelector((state) =>
    isOtherSideOfSplitTransferTxn({
      txn,
      transactionsById: transactionsSelectors.getResourcesById(state),
    }),
  );

  const isBlock = variant === 'block';
  const acceptedSchedule = txn.source === 'SCHEDULED_TRANSACTION' && transactionsUtils.getMatchState(txn) !== 'matched';

  const UnlinkWrapper = acceptedSchedule ? QTip : React.Fragment;
  const unlinkProps = acceptedSchedule
    ? {
        title:
          'This is a reminder for a recurring bill. ' +
          'You can keep this transaction and it may match to a bank downloaded transaction when it comes in, or you can delete the transaction if you no longer want this reminder.',
        placement: 'right',
        arrow: true,
      }
    : {};

  if (model && txn.source !== 'SCHEDULED_TRANSACTION_PENDING') {
    return (
      <UnlinkWrapper {...unlinkProps}>
        <div className={cx(classes.recurringLabel, isBlock && classes.fullWidth)}>
          <div className={classes.recurringText}>
            <RepeatIcon className={classes.recurringImgClass} />
            <Typography variant='overlineRegular' className={classes.boldText}>
              {splitSeriesEnabled ? 'LINKED TO SERIES' : 'RECURRING [BILL/INCOME]'}
            </Typography>
            <div className={classes.displayFlex}>
              {splitSeriesEnabled && (
                <Typography variant='paragraphRegular' noWrap className={classes.seriesLine}>
                  Series:&nbsp;
                </Typography>
              )}
              <div className={classes.link}>
                <Typography id={'view-series-button'} onClick={viewRecurring} noWrap variant='paragraphRegular'>
                  {model?.transaction?.payee || 'Unknown'}
                </Typography>
                <ArrowForwardIosRoundedIcon className={classes.rightArrowClass} />
              </div>
            </div>
          </div>
          <ButtonBase
            className={classes.menu}
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
            }}
            focusRipple
            id='recurring-settings-button'
          >
            <MenuButton className={classes.light} />
          </ButtonBase>
          {menuAnchor && (
            <Menu
              anchorEl={menuAnchor}
              id='recurring-settings-menu'
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              onClick={(e) => e.stopPropagation()}
            >
              {!hideUnlinkSeriesOption && (
                <MenuItem
                  id={'details-investment-menu-item'}
                  onClick={() => {
                    unlinkRecurring();
                    setMenuAnchor(null);
                  }}
                  disabled={acceptedSchedule}
                >
                  <ListItemIcon>
                    <LinkOff />
                  </ListItemIcon>
                  <Typography variant='bodyRegular'>Unlink</Typography>
                </MenuItem>
              )}
              {!hideUnlinkSeriesOption && <Divider />}
              <MenuItem
                id={'details-investment-menu-item'}
                onClick={() => {
                  setEditSeries();
                  setMenuAnchor(null);
                }}
              >
                <ListItemIcon>
                  <EditIcon />
                </ListItemIcon>
                <Typography variant='bodyRegular'>Edit Series</Typography>
              </MenuItem>
              <MenuItem
                id={'details-investment-menu-item'}
                onClick={() => {
                  viewRecurring();
                  setMenuAnchor(null);
                }}
              >
                <ListItemIcon>
                  <BarChartIcon />
                </ListItemIcon>
                <Typography variant='bodyRegular'>View Series History</Typography>
              </MenuItem>
            </Menu>
          )}
        </div>
      </UnlinkWrapper>
    );
  }
  if (isBill) {
    return (
      <QButton
        id='unmark-bill'
        onClick={unMarkAsBill}
        className={cx(classes.markAsRecurring, isBlock && classes.fullWidth)}
      >
        <RemoveCircleIcon className={classes.spacedIcon} /> Unmark as One-time bill
      </QButton>
    );
  }
  if (!model && !isBill) {
    if (isBlock) {
      return (
        <QButton
          id='make-recurring-button'
          onClick={markRecurring}
          disabled={disabledRecurring}
          className={classes.markAsRecurringBlock}
          style={{ textTransform: 'none' }}
          startIcon={<RepeatIcon className={classes.recurringBlockIcon} />}
        >
          Mark as recurring
        </QButton>
      );
    }
    return (
      <QButton
        id='make-recurring-button'
        onClick={markRecurring}
        disabled={disabledRecurring}
        className={classes.markAsRecurring}
      >
        <RepeatIcon className={classes.imgClass} /> Mark As Recurring
      </QButton>
    );
  }
  return null;
}

export default memo(RecurringLinkRow);
